import { useState } from 'react'
import { Alert, Badge, Button } from '../ui/primitives'

export default function BootstrapSetupStep({ step, onBack, onNext, onStart, onRefresh }) {
  const [startingAgentId, setStartingAgentId] = useState(null)
  const [error, setError] = useState(null)
  const appliances = step?.appliances || []
  const complete = step?.status === 'complete'

  const start = async (agentId) => {
    setStartingAgentId(agentId)
    setError(null)
    try {
      await onStart(agentId)
    } catch {
      setError('We couldn\'t start initialization. Try again.')
    } finally {
      setStartingAgentId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Encrypt and initialize an appliance</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">
            Operations prepares the required security settings. Start initialization when an appliance is ready.
          </p>
        </div>
        {complete && <Badge color="info">Complete</Badge>}
      </div>

      {(error || step?.error) && <Alert color="danger">{error || 'Initialization status could not be refreshed. Try again.'}</Alert>}

      <div className="space-y-2">
        {appliances.map((appliance) => {
          const active = ['pending', 'in_progress'].includes(appliance.bootstrap_status)
          const initialized = appliance.bootstrap_status === 'completed'
          const securityReady = appliance.artifacts_ready || initialized
          return (
            <div key={appliance.id} className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-[var(--app-text)]">{appliance.name}</div>
                </div>
                <Badge color={initialized ? 'info' : securityReady ? 'warning' : 'secondary'}>
                  {initialized ? 'Initialized' : active ? 'Initializing' : securityReady ? 'Ready to initialize' : 'Preparing'}
                </Badge>
              </div>
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <div><span className="text-[var(--app-text-muted)]">Security setup</span><div>{initialized ? 'Complete' : securityReady ? 'Ready' : 'Preparing'}</div></div>
                <div><span className="text-[var(--app-text-muted)]">Initialization</span><div>{initialized ? 'Complete' : active ? 'In progress' : securityReady ? 'Ready to start' : 'Waiting'}</div></div>
              </div>
              {appliance.artifact_delivery_error && <Alert color="warning">Preparation is taking longer than expected. Operations will keep trying.</Alert>}
              {appliance.artifacts_ready && !active && !initialized && (
                <Button color="primary" disabled={startingAgentId !== null} onClick={() => start(appliance.id)}>
                  {startingAgentId === appliance.id ? 'Starting…' : 'Start initialization'}
                </Button>
              )}
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button onClick={onBack}>Back</Button>
        <div className="flex gap-2">
          <Button onClick={onRefresh}>Refresh status</Button>
          {complete && <Button color="primary" onClick={onNext}>Next</Button>}
        </div>
      </div>
    </div>
  )
}
